/**
 * Service for Websites API
 * Manages user websites with AI-generated descriptions
 */

import { getUser } from './authService';
import { analyzeWebsite, translateWebsiteContent, isGeminiAvailable as checkGeminiAvailable } from './geminiService';
import { enTetesAuth } from './enTetesAuth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/websites`
    : 'https://apicabaza.creago.be/rest/cabaza/websites';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le site visé appartient bien à l'appelant.
 * Il lui faut donc une identité prouvée : le JWT signé au login, et non le
 * `peopleId` que le corps de la requête déclarait jusqu'ici — ce dernier
 * venait du client, et se remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Map of lowercase keys to camelCase
 */
const keyMapping = {
    peopleid: 'peopleId',
    createdat: 'createdAt',
    updatedat: 'updatedAt',
    firstname: 'firstName',
    lastname: 'lastName',
    peopleuuid: 'peopleUuid',
    // Multilingual Title fields
    title_fr: 'title_fr',
    title_en: 'title_en',
    title_nl: 'title_nl',
    title_de: 'title_de',
    title_es: 'title_es',
    title_it: 'title_it',
    title_pt: 'title_pt',
    title_el: 'title_el',
    // Multilingual Description fields
    description_fr: 'description_fr',
    description_en: 'description_en',
    description_nl: 'description_nl',
    description_de: 'description_de',
    description_es: 'description_es',
    description_it: 'description_it',
    description_pt: 'description_pt',
    description_el: 'description_el',
    // Multilingual Keywords fields
    keywords_fr: 'keywords_fr',
    keywords_en: 'keywords_en',
    keywords_nl: 'keywords_nl',
    keywords_de: 'keywords_de',
    keywords_es: 'keywords_es',
    keywords_it: 'keywords_it',
    keywords_pt: 'keywords_pt',
    keywords_el: 'keywords_el',
    // Multilingual Category fields
    category_fr: 'category_fr',
    category_en: 'category_en',
    category_nl: 'category_nl',
    category_de: 'category_de',
    category_es: 'category_es',
    category_it: 'category_it',
    category_pt: 'category_pt',
    category_el: 'category_el',
    // Contact fields (renamed from ai prefix)
    contactemail: 'contactEmail',
    contactphone: 'contactPhone',
    address: 'address',
    sociallinks: 'socialLinks',
    snapshotpath: 'snapshotPath',
    snapshotdate: 'snapshotDate',
    ispublic: 'isPublic',
    isactive: 'isActive',
    processed: 'processed',
    processedat: 'processedAt',
    processerror: 'processError',
    displayorder: 'displayOrder',
    viewcount: 'viewCount'
};

/**
 * Normalize object keys to camelCase
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                const newKey = keyMapping[lowerKey] || lowerKey;
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

/**
 * Get user's websites
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Websites array
 */
export const getMyWebsites = async (options = {}) => {
    try {
        const user = getUser();
        if (!user?.id) {
            console.error('No user logged in');
            return [];
        }

        const params = new URLSearchParams({
            peopleId: user.id.toString()
        });

        if (options.isPublic !== undefined) {
            params.append('isPublic', options.isPublic.toString());
        }
        if (options.isActive !== undefined) {
            params.append('isActive', options.isActive.toString());
        }

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? []);
    } catch (error) {
        console.error('Error fetching websites:', error);
        return [];
    }
};

/**
 * Get a website by UUID (public)
 * @param {string} websiteUuid - Website UUID
 * @returns {Promise<Object|null>} Website object or null
 */
export const getWebsiteByUuid = async (websiteUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${websiteUuid}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? null);
    } catch (error) {
        console.error('Error fetching website:', error);
        return null;
    }
};

/**
 * Get websites for a user (public)
 * @param {string} peopleUuid - People UUID
 * @returns {Promise<Array>} Websites array
 */
export const getWebsitesByPeople = async (peopleUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people/${peopleUuid}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? []);
    } catch (error) {
        console.error('Error fetching websites:', error);
        return [];
    }
};

/**
 * Create a new website with all form fields
 * @param {Object} data - Website data
 * @param {string} data.url - Website URL
 * @param {string} data.title - Title
 * @param {string} data.description - Description
 * @param {string} data.keywords - Keywords
 * @param {string} data.category - Category
 * @param {string} data.language - Language code (fr, en, nl, etc.)
 * @param {boolean} data.isPublic - Public visibility
 * @returns {Promise<Object>} Result with created website
 */
export const createWebsite = async (data) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour ajouter un site web'
            };
        }

        // Simple payload - only basic fields
        const payload = {
            url: data.url,
            title: data.title || '',
            isPublic: data.isPublic ?? true
        };

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify(payload)
        });

        const text = await response.text();
        if (!text || text.trim() === '') {
            return {
                success: false,
                message: 'Service non disponible. Veuillez réessayer plus tard.'
            };
        }

        const result = JSON.parse(text);

        if (!response.ok) {
            if (response.status === 409) {
                return {
                    success: false,
                    duplicate: true,
                    existingId: result.existingId || result.EXISTINGID,
                    message: result.message || result.MESSAGE || 'Ce site web est déjà enregistré'
                };
            }
            throw new Error(result.message || result.MESSAGE || 'Error creating website');
        }

        return {
            success: true,
            data: normalizeKeys(result.data ?? result.DATA),
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error creating website:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update a website
 * @param {number} websiteId - Website ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Result with updated website
 */
export const updateWebsite = async (websiteId, updates) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour modifier un site web'
            };
        }

        const response = await fetch(`${API_BASE_URL}/${websiteId}`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify(updates)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message ?? result.MESSAGE ?? 'Error updating website');
        }

        return {
            success: true,
            data: normalizeKeys(result.data ?? result.DATA),
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating website:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update website multilingual data
 * @param {number} websiteId - Website ID
 * @param {Object} data - Multilingual fields to update
 * @returns {Promise<Object>} Result with updated website
 */
export const updateWebsiteData = async (websiteId, data) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour modifier un site web'
            };
        }

        const response = await fetch(`${API_BASE_URL}/${websiteId}/data`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify(data)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message ?? result.MESSAGE ?? 'Error updating website data');
        }

        return {
            success: true,
            data: normalizeKeys(result.data ?? result.DATA),
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating website data:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Delete a website
 * @param {number} websiteId - Website ID
 * @returns {Promise<Object>} Result
 */
export const deleteWebsite = async (websiteId) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour supprimer un site web'
            };
        }

        const response = await fetch(`${API_BASE_URL}/${websiteId}`, {
            method: 'DELETE',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting website:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Analyze a website with Gemini AI (via geminiService)
 * Generates content for ONE language only
 * @param {number} websiteId - Website ID
 * @param {string} url - Website URL to analyze
 * @param {string} language - Language code (fr, en, nl, de, es, it, pt, el)
 * @returns {Promise<Object>} AI analysis result for the specified language
 */
export const analyzeWebsiteWithAI = async (websiteId, url, language = 'fr') => {
    if (!checkGeminiAvailable()) {
        return {
            success: false,
            message: 'Clé API Gemini non configurée'
        };
    }

    try {
        // Single Gemini request generates content for one language
        const extractedData = await analyzeWebsite(url, language);

        // Update the website with the data for this language
        const updateResponse = await fetch(`${API_BASE_URL}/${websiteId}/data`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify(extractedData)
        });

        const updateResult = await updateResponse.json();

        return {
            success: updateResponse.ok,
            data: normalizeKeys(updateResult.data ?? updateResult.DATA),
            aiData: extractedData,
            message: updateResponse.ok ? 'Analyse terminée avec succès' : 'Erreur lors de la mise à jour'
        };

    } catch (error) {
        console.error('Error analyzing website with AI:', error);

        // Save error to database
        try {
            await fetch(`${API_BASE_URL}/${websiteId}/data`, {
                method: 'PUT',
                headers: enTetesAuth(false),
                credentials: 'include',
                headers: enTetesEcriture(),
                body: JSON.stringify({
                    ProcessError: error.message
                })
            });
        } catch (e) {
            console.error('Error saving AI error:', e);
        }

        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Translate website content to all other languages (background process)
 * @param {number} websiteId - Website ID
 * @param {Object} sourceContent - Content in source language { title, description, keywords, category }
 * @param {string} sourceLang - Source language code
 * @returns {Promise<Object>} Result
 */
export const translateWebsiteToAllLanguages = async (websiteId, sourceContent, sourceLang = 'fr') => {
    if (!checkGeminiAvailable()) {
        return {
            success: false,
            message: 'Clé API Gemini non configurée'
        };
    }

    try {
        // Get translations for all 7 other languages
        const translations = await translateWebsiteContent(sourceContent, sourceLang);

        // Update the website with all translations
        const updateResponse = await fetch(`${API_BASE_URL}/${websiteId}/data`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify(translations)
        });

        const updateResult = await updateResponse.json();

        return {
            success: updateResponse.ok,
            data: normalizeKeys(updateResult.data ?? updateResult.DATA),
            message: updateResponse.ok ? 'Traductions terminées' : 'Erreur lors de la mise à jour'
        };

    } catch (error) {
        console.error('Error translating website:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Generate a website snapshot using Puppeteer service
 * @param {number} websiteId - Website ID
 * @param {string} url - Website URL to capture
 * @returns {Promise<Object>} Snapshot URL result
 */
export const generateSnapshot = async (websiteId, url) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return { success: false, message: 'Vous devez être connecté' };
        }

        // Call the Puppeteer screenshot service
        const SCREENSHOT_SERVICE_URL = import.meta.env.VITE_SCREENSHOT_SERVICE_URL || 'https://apicabaza.creago.be/screenshot/screenshot';

        const screenshotResponse = await fetch(SCREENSHOT_SERVICE_URL, {
            method: 'POST',
            headers: enTetesAuth(),
            body: JSON.stringify({ url, websiteId })
        });

        const screenshotResult = await screenshotResponse.json();

        if (!screenshotResult.success) {
            throw new Error(screenshotResult.message || 'Erreur lors de la capture');
        }

        const snapshotUrl = screenshotResult.data?.publicUrl;

        // Update the website with the snapshot URL
        const response = await fetch(`${API_BASE_URL}/${websiteId}`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                snapshotPath: snapshotUrl,
                snapshotDate: new Date().toISOString()
            })
        });

        const result = await response.json();

        return {
            success: response.ok,
            snapshotUrl,
            data: normalizeKeys(result.data ?? result.DATA),
            message: response.ok ? 'Snapshot généré avec succès' : 'Erreur lors de la mise à jour'
        };
    } catch (error) {
        console.error('Error generating snapshot:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Check if Gemini API is available
 * @returns {boolean}
 */
export const isGeminiAvailable = () => {
    return checkGeminiAvailable();
};

export default {
    getMyWebsites,
    getWebsiteByUuid,
    getWebsitesByPeople,
    createWebsite,
    updateWebsite,
    updateWebsiteData,
    deleteWebsite,
    analyzeWebsiteWithAI,
    translateWebsiteToAllLanguages,
    generateSnapshot,
    isGeminiAvailable
};
